import Vue from 'vue'
import Vuex from 'vuex'
import DBUtil from '@utils/DBUtil'
import api from 'api'
import { hotkeys } from '../utils/hotkeys'

import modules from './modules'

Vue.use(Vuex)

var dayjs = require('dayjs')
var iconv = require('iconv-lite')

export default new Vuex.Store({
  modules,
  state: {
    is_mobile: document.body.clientWidth <= 640,
    is_ios: '',
    is_android: '',
    me: localStorage.me ? JSON.parse(localStorage.me) : '',
    memo_count: 0,
    daily_memo_count: {},
    tags: localStorage.tags == undefined ? [] : JSON.parse(localStorage.tags),
    expandedTagValues: localStorage.expandedTagValues ? JSON.parse(localStorage.expandedTagValues) : [],
    expandedPinnedTagValues: localStorage.expandedPinnedTagValues
      ? JSON.parse(localStorage.expandedPinnedTagValues)
      : [],
    pinned_tags: [],
    latest_tag_names: [],
    selected_tag: undefined,
    selected_source: null,
    start_date: null,
    end_date: null,
    type: null,
    notifications: [],
    wechat_info_loading: false,
    wechat_open_id: null,
    wechat_nickname: null,
    wechat_bind_qrcode_url: null,
    user_settings: {},
    theme: localStorage.theme ? localStorage.theme : 'auto',
    expand_all_tags: localStorage.expand_all_tags == undefined || localStorage.expand_all_tags == 1 ? 1 : 0,
    recent_tags: localStorage.recent_tags == undefined ? [] : JSON.parse(localStorage.recent_tags),
    emojis: localStorage.emojis == undefined ? [] : JSON.parse(localStorage.emojis),
    isOnLine: true, // 当前网络状态：true 在线,false 离线
    isOpenMemo: false,
    syncing: false,
    focused: false, // memo editor 是否获取到焦点
    zenMode: localStorage.zenMode ? localStorage.zenMode : 'off', // 是否开启 Zen Mode
    editMemoSlug: '', // 当前编辑的 memo slug
    passwordCode: localStorage.passwordCode ? localStorage.passwordCode : '-1', // 当前是否在密码模式下
    lock: localStorage.lock ? localStorage.lock : 0, // 当前是否锁定
    trayPinned: localStorage.trayPinned ? localStorage.trayPinned : false, // tray 是否固定
    exportProgress: 0, // 导出进度
    exportTotal: 0, // 导出总数
    exportTime: 0, // 导出耗时
    cancelExport: false, // 取消导出
    advancedSearchQuery: localStorage.advancedSearchQuery ? JSON.parse(localStorage.advancedSearchQuery) : null, // 高级搜索
    sidebarShow: localStorage.sidebarShow ? localStorage.sidebarShow : 1, // 侧边栏显示
    hotkeys: localStorage.hotkeys ? JSON.parse(localStorage.hotkeys) : hotkeys, // 快捷键
    downloadPrompt: localStorage.downloadPrompt ? localStorage.downloadPrompt : 1, // 下载提示
    pinnedTip: localStorage.pinnedTip ? localStorage.pinnedTip : 1, // pinned 提示
    clickDownloadDot: localStorage.clickDownloadDot ? localStorage.clickDownloadDot : 1, // 点击下载
    pinned_selected_tag: undefined, // pinned 选中的 tag
    emoji_tag: localStorage.emoji_tag ? JSON.parse(localStorage.emoji_tag) : null, // emoji tag
    review_active: localStorage.review_active ? localStorage.review_active : 2, // 通知
    review_memo_slugs: localStorage.review_memo_slugs ? JSON.parse(localStorage.review_memo_slugs) : [], // 回顾 memo slug 集合
    startUp: localStorage.startUp ? localStorage.startUp : 'off', // 开机启动
    membership_referral: null, // 来源,
    first_wx_review: localStorage.first_wx_review ? localStorage.first_wx_review : 0, // 首次打开微信回顾
    showReviewSetting: false, // 是否显示回顾设置
    used_day_count: 0, // 使用天数
    lastSyncDate: localStorage.lastSyncDate ? localStorage.lastSyncDate : null, // 上次同步日期
  },
  getters: {
    me: (state) => {
      return state.me
    },
    is_pro: (state) => {
      if (!state.me.pro_expired_at) return false
      return dayjs(state.me.pro_expired_at).isAfter(dayjs())
    },
    PRO_new_user: (state) => {
      // PRO新用户指：注册天数<=14天 且 是PRO 的用户
      return dayjs(state.me.pro_expired_at).isAfter(dayjs()) && dayjs().diff(dayjs(state.me.created_at), 'day') <= 14
    },
    isOnLine: (state) => {
      return state.isOnLine
    },
    isOpenMemo: (state) => {
      return state.isOpenMemo
    },
    syncing: (state) => {
      return state.syncing
    },
    zen_mode: (state) => {
      return state.zenMode
    },
    lock: (state) => {
      return state.lock
    },
    password_code: (state) => {
      return state.passwordCode
    },
    isElectron: () => !!process.env.IS_ELECTRON,
    isProduction: () => process.env.NODE_ENV == 'production',
    trayPinned: (state) => {
      return state.trayPinned
    },
    showReviewSetting: (state) => {
      return state.showReviewSetting
    },
    // 注册时间小于等于 14 天
    is_new_user: (state) => {
      const userRegisterStr = dayjs(state.me.created_at).format('YYYY-MM-DD')
      return dayjs().diff(userRegisterStr, 'day') <= 14
    },
    used_day_count: (state) => {
      return state.used_day_count
    },
    isApplePrivateEmail: (state) => {
      return state.me && state.me.email ? state.me.email.endsWith('@privaterelay.appleid.com') : false
    },
  },
  mutations: {
    setMe(state, me) {
      let language = me.language
      // 注册账号时，language 为 zh 和 账号详情语言下拉选项无法匹配，所以需要转换 zh -> zh-cn
      // 统一 zh-cn 改为 zh
      if (language == 'zh-cn') {
        language = 'zh'
      }

      const isPro = this.getters.is_pro
      if (!isPro) {
        // 移除密码锁
        state.lock = false
        localStorage.lock = false
      }

      state.me = {
        ...state.me,
        ...me,
        language,
      }

      localStorage.me = JSON.stringify(state.me)
    },
    async removeMe(state) {
      state.me = ''
      state.zenMode = 'off'
      state.exportProgress = 0
      const keysToRemove = [
        'zenMode',
        'theme',
        'me',
        'hotkeys',
        'sidebarShow',
        'tags',
        'downloadPrompt',
        'exportProgress',
        'emoji_tag',
        'review_active',
        'dailyReviewList',
        'is_get_list',
        'nowDay',
        'rest_number',
        'count',
        'oldReviewSlugs',
        'lock',
        'showBindWeixinTips',
        'memoHelpTip',
        'tagHelpTip',
        'membershipHelpTip',
        'dailyReviewHelpTip',
        'memoHelpDialog',
        'tagHelpDialog',
        'heatgridHelpDialog',
        'useSuggestions',
        'openDailyReview',
        'daily_review_list',
        'daily_review_progress',
        'enable_homepage_notification',
        'daily_review_reminder',
        'notification_id',
        'lastSyncDate',
        'hasRefresh',
        'keywordsList',
      ]

      // 使用 Promise.all 来并行删除 localStorage 中的项
      const removeItemPromises = keysToRemove.map((key) => localStorage.removeItem(key))

      await Promise.all(removeItemPromises)
      await DBUtil.clearDB()
      if (this.getters.isElectron && this.getters.isProduction) {
        return (window.location.href = 'flomo://./index.html')
      }
      window.location.replace('/')
    },
    setMemoCount(state, memo_count) {
      state.memo_count = memo_count
    },
    setDailyMemoCount(state, daily_memo_count) {
      state.daily_memo_count = daily_memo_count
    },
    setUsedDayCount(state, used_day_count) {
      state.used_day_count = used_day_count
    },
    incrMemoCount(state, incr = 1) {
      state.memo_count += incr
      let today_str = dayjs().format('YYYY-MM-DD')
      if (!state.daily_memo_count.hasOwnProperty(today_str)) {
        state.daily_memo_count[today_str] = 0
      }
      state.daily_memo_count[today_str] += incr
    },
    decrMemoCount(state, decr = 1) {
      state.memo_count -= decr
    },
    decrMemoCountOfDate(state, date, decr = 1) {
      if (state.daily_memo_count.hasOwnProperty(date)) {
        state.daily_memo_count[date] -= decr
      }
    },
    setTags(state, tags) {
      state.tags = tags
      localStorage.tags = JSON.stringify(state.tags)
    },
    addTagNames(state, new_tag_names) {
      for (const index in new_tag_names) {
        let new_tag_name = new_tag_names[index]
        let new_tag_name_exists = false
        for (const key in state.tags) {
          const tag = state.tags[key]
          if (tag.name == new_tag_name) {
            new_tag_name_exists = true
          }
        }

        if (!new_tag_name_exists) {
          state.tags.push({ name: new_tag_name })
        }
      }

      for (const index in state.tags) {
        const tag = state.tags[index]
        // tag.name utf-8 to gbk
        tag.first_letter = iconv.encode(tag.name, 'gbk').toString('hex')
      }

      // tags sort by name abc order and order
      state.tags.sort((a, b) => {
        if (a.order > 0 && b.order > 0) {
          return a.order - b.order
        } else if (a.order > 0) {
          return -1
        } else if (b.order > 0) {
          return 1
        }
        if (a.first_letter < b.first_letter) return -1
        if (a.first_letter > b.first_letter) return 1
        return 0
      })
    },

    addExpandedTagValue(state, tagValue) {
      state.expandedTagValues.unshift(tagValue)
      localStorage.expandedTagValues = JSON.stringify(state.expandedTagValues)
    },
    removeExpandedTagValue(state, tagValue) {
      while (state.expandedTagValues.indexOf(tagValue) != -1) {
        state.expandedTagValues.splice(state.expandedTagValues.indexOf(tagValue), 1)
      }
      localStorage.expandedTagValues = JSON.stringify(state.expandedTagValues)
    },
    addExpandedPinnedTagValue(state, tagValue) {
      state.expandedPinnedTagValues.unshift(tagValue)
      localStorage.expandedPinnedTagValues = JSON.stringify(state.expandedPinnedTagValues)
    },
    removeExpandedPinnedTagValue(state, tagValue) {
      while (state.expandedPinnedTagValues.indexOf(tagValue) != -1) {
        state.expandedPinnedTagValues.splice(state.expandedPinnedTagValues.indexOf(tagValue), 1)
      }
      localStorage.expandedPinnedTagValues = JSON.stringify(state.expandedPinnedTagValues)
    },
    setLatestTagNames(state, latest_tag_names) {
      state.latest_tag_names = latest_tag_names
    },
    addLatestTagNames(state, new_tag_names) {
      for (const index in new_tag_names) {
        let tag_name = new_tag_names[index]
        if (!state.latest_tag_names.includes(tag_name)) {
          state.latest_tag_names.unshift(tag_name)
        }
      }
    },
    setPinnedTags(state, pinned_tags) {
      state.pinned_tags = pinned_tags
    },
    addPinnedTag(state, pinned_tag) {
      const { value, icon } = pinned_tag
      state.pinned_tags.unshift({
        name: value,
        icon_type: 'emoji',
        icon_value: icon,
      })
    },
    removePinnedTag(state, pinned_tag_name) {
      for (const key in state.pinned_tags) {
        const pinned_tag = state.pinned_tags[key]
        if (pinned_tag.name == pinned_tag_name) {
          state.pinned_tags.splice(key, 1)
        }
      }
    },
    setSelectedTag(state, seletectTag) {
      state.selected_tag = seletectTag
    },
    setSelectedSource(state, selected_source) {
      state.selected_source = selected_source
    },
    setStartDate(state, start_date) {
      state.start_date = start_date
    },
    setEndDate(state, end_date) {
      state.end_date = end_date
    },
    setType(state, type) {
      state.type = type
    },
    setNotifications(state, notifications) {
      state.notifications = notifications
    },
    setWechatInfoLoading(state, wechat_info_loading) {
      state.wechat_info_loading = wechat_info_loading
    },
    setWechatOpenId(state, wechatOpenId) {
      state.wechat_open_id = wechatOpenId
    },
    setWechatNickname(state, wechatNickname) {
      state.wechat_nickname = wechatNickname
    },
    setWechatBindQrcodeUrl(state, wechatBindQrcodeUrl) {
      state.wechat_bind_qrcode_url = wechatBindQrcodeUrl
    },
    setUserSettings(state, user_settings) {
      state.user_settings = user_settings
    },
    setUserSetting(state, setting) {
      let is_exists = false
      for (let i = 0; i < state.user_settings.length; i++) {
        if (state.user_settings[i].type != setting.type) continue
        Vue.set(state.user_settings, i, setting)
        is_exists = true
      }
      if (!is_exists) {
        state.user_settings.push(setting)
      }
    },
    setTheme(state, theme) {
      state.theme = theme
      localStorage.theme = state.theme
    },
    expandAllTags(state, expand) {
      state.expand_all_tags = expand
      localStorage.expand_all_tags = state.expand_all_tags ? 1 : 0
    },
    set_recent_tags(state, recent_tags) {
      state.recent_tags.push(recent_tags)
      state.recent_tags = [...new Set(state.recent_tags.slice(0, 18))]
      localStorage.recent_tags = JSON.stringify(state.recent_tags)
    },
    save_emoji(state, tags) {
      state.emojis = tags
      localStorage.emojis = JSON.stringify(state.emojis)
    },
    setQuery(state, query) {
      state.query = query
    },
    setIsOnLine(state, isOnLine) {
      state.isOnLine = isOnLine
    },
    setIsOpenMemo(state, isOpenMemo) {
      state.isOpenMemo = isOpenMemo
    },
    setSyncing(state, syncing) {
      state.syncing = syncing
    },
    setFocused(state, focused) {
      state.focused = focused
    },
    setEditMemoSlug(state, editMemoSlug) {
      state.editMemoSlug = editMemoSlug
    },
    setZenMode(state, zenMode) {
      state.zenMode = zenMode
      localStorage.zenMode = state.zenMode
    },

    setPasswordCode(state, passwordCode) {
      console.log('setPasswordTime', passwordCode, dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'))
      state.passwordCode = passwordCode
      localStorage.passwordCode = state.passwordCode
    },
    setLock(state, lock) {
      // check is pro
      if (this.getters.is_pro) {
        state.lock = lock
        localStorage.lock = state.lock
        console.log(lock == 1 ? '锁屏' : '解锁', dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss'))
      } else {
        // if not pro, lock is always false
        state.lock = false
        localStorage.lock = false
      }
    },
    setTrayPinned(state, trayPinned) {
      state.trayPinned = trayPinned
      localStorage.trayPinned = state.trayPinned
    },
    setExportProgress(state, exportProgress) {
      state.exportProgress = exportProgress
    },
    setExportTotal(state, exportTotal) {
      state.exportTotal = exportTotal
    },
    setExportTime(state, exportTime) {
      state.exportTime = exportTime
    },
    setCancelExport(state, cancelExport) {
      state.cancelExport = cancelExport
    },
    setAdvancedSearchQuery(state, advancedSearchQuery) {
      state.advancedSearchQuery = advancedSearchQuery
      localStorage.advancedSearchQuery = state.advancedSearchQuery
    },
    setSidebarShow(state, sidebarShow) {
      state.sidebarShow = sidebarShow
      localStorage.sidebarShow = state.sidebarShow
    },
    delHotkeyByAction(state, action) {
      for (const key in state.hotkeys) {
        const hotkey = state.hotkeys[key]
        if (hotkey.action == action) {
          hotkey.key = ''
        }
      }
    },
    setHotkeys(state, hotkeys) {
      for (const key in state.hotkeys) {
        const hotkey = state.hotkeys[key]
        if (hotkey.action == hotkeys.action) {
          hotkey.key = hotkeys.key
        }
      }
    },
    saveHotkeys(state, saveHotkeys) {
      state.hotkeys = saveHotkeys
      localStorage.hotkeys = JSON.stringify(saveHotkeys)
      if (state.isElectron) {
        window.$useInfo.setItem('HotKeys', JSON.stringify(saveHotkeys))
      }
    },
    setDownloadPrompt(state, downloadPrompt) {
      state.downloadPrompt = downloadPrompt
      localStorage.downloadPrompt = state.downloadPrompt
    },
    setPinnedTip(state, pinnedTip) {
      state.pinnedTip = pinnedTip
      localStorage.pinnedTip = state.pinnedTip
    },
    setClickDownloadDot(state, clickDownloadDot) {
      state.clickDownloadDot = clickDownloadDot
      localStorage.clickDownloadDot = state.clickDownloadDot
    },
    setPinnedSelectedTag(state, pinned_selected_tag) {
      state.pinned_selected_tag = pinned_selected_tag
      // localStorage.selected_tag = state.selected_tag;
    },
    setEmojiTag(state, emoji_tag) {
      state.emoji_tag = emoji_tag
      localStorage.emoji_tag = JSON.stringify(state.emoji_tag)
    },
    setReviewActive(state, review_active) {
      state.review_active = review_active
      localStorage.review_active = state.review_active
    },
    setReviewMemoSlugs(state, review_memo_slugs) {
      state.review_memo_slugs = review_memo_slugs
      localStorage.review_memo_slugs = JSON.stringify(state.review_memo_slugs)
    },
    addReviewMemoSlugs(state, review_memo_slug) {
      // 已存在的不添加
      if (state.review_memo_slugs.indexOf(review_memo_slug) > -1) return
      state.review_memo_slugs.push(review_memo_slug)
      localStorage.review_memo_slugs = JSON.stringify(state.review_memo_slugs)
    },

    setStartUp(state, startUp) {
      state.startUp = startUp
      localStorage.startUp = state.startUp
    },
    setMembershipReferral(state, membership_referral) {
      state.membership_referral = membership_referral
    },
    setNotificationReaded(state, notification_id) {
      for (const key in state.notifications) {
        const notification = state.notifications[key]
        if (notification.id == notification_id) {
          notification.readed = 1
        }
      }
    },
    setFirstWxReview(state, first_wx_review) {
      state.first_wx_review = first_wx_review
      localStorage.first_wx_review = state.first_wx_review
    },
    setShowReviewSetting(state, showReviewSetting) {
      state.showReviewSetting = showReviewSetting
    },
    updateLastSyncDate(state, date) {
      state.lastSyncDate = date
      localStorage.lastSyncDate = state.lastSyncDate
    },
  },
  actions: {
    async loadStat({ state, commit, rootState }, user_id) {
      let stats = await DBUtil.getStats()
      let memo_count = await DBUtil.getMemoCount()
      commit('setMemoCount', memo_count)
      commit('setDailyMemoCount', stats)
      const userRegisterDate = dayjs(state.me.created_at).format('YYYY-MM-DD')
      const daysDiff = Math.ceil(dayjs().diff(userRegisterDate, 'day', true))
      commit('setUsedDayCount', daysDiff)
    },
    async loadTags({ commit }, user_id) {
      let tags = await DBUtil.getTags()

      commit('setTags', tags)
      let emoji_tag = {}

      tags.map((tag) => {
        if (tag.icon_value) {
          emoji_tag[tag.name] = tag.icon_value
        }
      })
      commit('setEmojiTag', emoji_tag)
      // await Vue.prototype.$http
      // 	.get("/api/tag", {
      // 		params: {
      // 			user_id: user_id,
      // 		},
      // 	})
      // 	.then((response) => {
      // 		if (response.data && response.data.code === 0) {
      // 			commit("setTags", response.data.tags);
      // 		}
      // 	});
    },
    async loadLatestTags({ commit }, user_id) {
      let tagNames = await DBUtil.loadLatestTagNames()
      commit('setLatestTagNames', tagNames)

      // await Vue.prototype.$http.get("/api/tag/latest").then((response) => {
      // 	if (response.data && response.data.code === 0) {
      // 		commit("setLatestTagNames", response.data.tag_names);
      // 	}
      // });
    },
    async loadPinnedTags({ commit }) {
      // await Vue.prototype.$http.get("/api/tag/pinned").then((response) => {
      // 	if (response.data && response.data.code === 0) {
      // 		commit("setPinnedTags", response.data.tags);
      // 	}
      // });

      let tags = await DBUtil.getPinnedTags()
      commit('setPinnedTags', tags)
    },
    async loadNotifications({ commit }) {
      api.call('GET', '/notification/mine', {}, (response) => {
        if (response.data && response.data.code === 0) {
          commit('setNotifications', response.data.data)
        }
      })
    },
    async loadWechatInfo({ commit }) {
      commit('setWechatInfoLoading', true)
      commit('setWechatOpenId', null)
      commit('setWechatNickname', null)
      commit('setWechatBindQrcodeUrl', null)

      api.call('GET', '/wechat/info', {}, (response) => {
        if (response.data && response.data.code === 0) {
          commit('setWechatOpenId', response.data.data.wechat_open_id)
          commit('setWechatNickname', response.data.data.wechat_nickname)
          commit('setWechatBindQrcodeUrl', response.data.data.bind_qrcode_url)
          commit('setWechatInfoLoading', false)
        }
      })
    },
    async unbindWechat({ commit }) {
      api.call('POST', '/wechat/unbind', {}, (response) => {
        if (response.data && response.data.code === 0) {
          commit('setWechatOpenId', null)
          commit('setWechatNickname', null)
        }
      })
    },
    async loadUserSettings({ commit }, callback = null) {
      api.call('GET', '/user_setting/mine', {}, (response) => {
        if (response.data && response.data.code == 0) {
          commit('setUserSettings', response.data.data)
        }
        if (callback) callback(response)
      })
    },
    async get_emojis({ commit }) {
      api.call('GET', '/emoji', {}, (response) => {
        if (response.data && response.data.code == 0) {
          commit('save_emoji', response.data.data)
        }
      })
    },
    async setIsOnLine({ commit }, isOnLine) {
      commit('setIsOnLine', isOnLine)
    },
    async setIsOpenMemo({ commit }, isOpenMemo) {
      commit('setIsOpenMemo', isOpenMemo)
    },
    async setSyncing({ commit }, syncing) {
      commit('setSyncing', syncing)
    },
    async setFocused({ commit }, focused) {
      commit('setFocused', focused)
    },
    async setEditMemoSlug({ commit }, editMemoSlug) {
      commit('setEditMemoSlug', editMemoSlug)
    },
    async setZenMode({ commit }, zenMode) {
      commit('setZenMode', zenMode)
    },
    async setPasswordCode({ commit }, passwordCode) {
      commit('setPasswordCode', passwordCode)
    },
    async setLock({ commit }, lock) {
      commit('setLock', lock)
    },
    async unlock({ commit }, password) {
      api.call('post', '/user/check_password/', { password }, (response) => {
        if (response.data && response.data.code == 0) {
          commit('setLock', 0)
        }
      })
    },
    async setTrayPinned({ commit }, trayPinned) {
      commit('setTrayPinned', trayPinned)
    },
    resetFilter({ commit, dispatch }) {
      commit('setStartDate', null)
      commit('setEndDate', null)
      commit('setSelectedSource', null)
      commit('setPinnedSelectedTag', null)
      commit('setSelectedTag', null)
      commit('setType', null)
      commit('setQuery', null)
      dispatch('thirdParty/clearInterval')
      // 清空高级搜索的条件
      localStorage.removeItem('advancedSearchQuery')
      dispatch('search/clearAdvancedSearchCondition')
    },
    logout({ commit }) {
      commit('removeMe')
    },
    async updateUserInfo({ commit }, userinfo) {
      commit('setMe', userinfo)
    },
    async getUserInfo({ state, commit, dispatch }) {
      const currentDate = new Date().toLocaleDateString()
      if (state.lastSyncDate !== currentDate) {
        // 如果上次同步的日期不是今天，可以进行同步操作
        // 更新上次同步的日期为今天
        dispatch('getUser')
        commit('updateLastSyncDate', currentDate)
      }
    },
    async getUser({ state, commit }) {
      api.call('get', '/user/me', {}, (response) => {
        if (response.data && response.data.code == 0) {
          commit('setMe', response.data.data)
        }
      })
    },
  },
})
